import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
import { IDropLocation } from "./useDropLocations";
import { IMarketPricing } from "./useMarketPricing";
import { useAppLoading } from "./useAppLoading";
import { LOG } from "../utils/logUtil";
import axios from "axios";
import { useCurrentLocation } from "./useCurrentLocation";

export interface ISummaryStats {
  _id: string;
  location: IDropLocation;
  filterType: "ALL" | "YEARLY" | "MONTHLY" | "WEEKLY" | "LAST_THIRTY_DAYS";
  year: number;
  month: number;
  week: number;
  isLastMonth: boolean;
  // User stats
  downloads: number;
  allUsers: number;
  regularUsers: number;
  activeUsers: number;
  tryOutUsers: number;
  churn: number;
  monthlyActiveUsers: number;
  inactiveUsers: number;
  activeUsersCumulative: number;
  materialsActiveUsers: number;
  statieActiveUsers: number;
  // Material stats
  totalMaterialDrops: number;
  totalNonCumMaterialDrops: number;
  totalMaterialVolume: number;
  totalMaterialRevenue: number;
  materialsVolume: {
    material: string | IMarketPricing;
    volume: number;
  }[];
  materialsConversion: {
    material: string | IMarketPricing;
    users: number;
  }[];
  materialsRevenue: {
    material: string | IMarketPricing;
    revenue: number;
  }[];
  // Dropbot stats
  totalDropbotDrops: number;
  totalNonCumDropbotDrops: number;
  totalDropbotVolume: number;
  totalDropbotRevenue: number;
  // Statie stats
  totalStatieDrops: number;
  totalNonCumStatieDrops: number;
  totalStatieItems: number;
  totalStatieRevenue: number;
  // Parcels
  totalParcelsVolume: number;
  totalParcelsRevenue: number;
  //
  createdAt: Date;
  updatedAt: Date;
}
export interface ISummaryStatsV2 {
  _id: string;
  filterType: "ALL" | "YEARLY" | "MONTHLY" | "WEEKLY";
  year: number;
  month: number;
  week: number;
  isLastMonth: boolean;
  // User stats
  downloads: number;
  allUsers: number;
  regularDroppers: number; // regularUsers
  activeDroppers: number; // activeUsers
  inactiveDroppers: number; // inactiveUsers
  droppers: number; // tryOutUsers
  churn: number;
  monthlyDroppers: number; //monthlyActiveUsers
  activeUsersCumulative: number;
  materialsRegularDroppers: number;
  materialsActiveDroppers: number; // materialsActiveUsers
  materialsInactiveDroppers: number;
  // Material stats
  totalMaterialDrops: number;
  totalNonCumMaterialDrops: number;
  totalMaterialVolume: number;
  totalMaterialRevenue: number;
  materialsVolume: {
    material: string | IMarketPricing;
    volume: number;
  }[];
  materialsConversion: {
    material: string | IMarketPricing;
    users: number;
  }[];
  materialsRevenue: {
    material: string | IMarketPricing;
    revenue: number;
  }[];
  // Dropbot stats
  totalDropbotDrops: number;
  totalNonCumDropbotDrops: number;
  totalDropbotVolume: number;
  totalDropbotRevenue: number;
  // Statie stats
  totalStatieDrops: number;
  totalNonCumStatieDrops: number;
  totalStatieItems: number;
  totalStatieRevenue: number;
  statieRegularDroppers: number;
  statieActiveDroppers: number; // statieActiveUsers
  statieInactiveDroppers: number;
  // Parcels
  totalParcelsVolume: number;
  totalParcelsRevenue: number;
  // Locations
  fullServiceLocations: number;
  selfServiceLocations: number;
  containers: number;
  //
  createdAt: Date;
  updatedAt: Date;
}

export interface IKPIData {
  downloads: number;
  regularDroppers: number;
  monthlyVisitors: number;
  moneyPaidOut: number;
  totalDrops: number;
  totalCollectedWeight: number;
  totalDonationAccounts: number;
  totalMoneyDonated?: number; // cents, cumulative
}

export interface IKPIResponse {
  cumulative: IKPIData;
  nonCumulative: IKPIData;
}

export interface GETSummaryStatsProps {
  period:
  | "all"
  | "yearly"
  | "quarterly"
  | "monthly"
  | "weekly"
  | "lastTwelveMonths";
  year?: number;
  fromYear?: number;
  toYear?: number;
  month?: number;
  fromMonth?: number;
  toMonth?: number;
  fromWeek?: number;
  toWeek?: number;
  quarter?: "Q1" | "Q2" | "Q3" | "Q4";
  week?: Date;
  selectedFilters?: string[];
  selectedStores?: string[];
  storeType?: "all" | "fs" | "ss" | "c";
}

interface IStore {
  // State
  allTimeSummaryStats: ISummaryStats[];
  yearlyLocationStats: ISummaryStats[];
  monthlyLocationStats: ISummaryStats[];
  weeklyLocationStats: ISummaryStats[];
  lastTwelweMonthsLocationStats: ISummaryStats[];
  allTimeSummaryStatsV2: ISummaryStatsV2[];
  yearlyLocationStatsV2: ISummaryStatsV2[];
  monthlyLocationStatsV2: ISummaryStatsV2[];
  weeklyLocationStatsV2: ISummaryStatsV2[];
  kpiStats: IKPIResponse | null;
  // Loaders
  isSummaryFilteringLoading: boolean;
  isKpiLoading: boolean;
  // Functions
  getSummaryStats: () => void;
  recalculateAllTimeSummaryStats: () => void;
  recalculateYearlySummaryStats: () => void;
  recalculateMonthlySummaryStats: (year: number, month: number) => void;
  recalculateWeeklySummaryStats: (year: number, month: number) => void;
  recalculatelastTwelveMonthsSummaryStats: () => void;
  getSummaryStatsByFiltering: ({
    period,
    year,
    fromYear,
    toYear,
    month,
    fromMonth,
    toMonth,
    fromWeek,
    toWeek,
    quarter,
    week,
    selectedFilters,
    selectedStores,
    storeType,
  }: GETSummaryStatsProps) => void;
  getKPIStats: ({
    period,
    year,
    fromYear,
    toYear,
    month,
    fromMonth,
    toMonth,
    fromWeek,
    toWeek,
    quarter,
    week,
    selectedFilters,
    selectedStores,
    storeType,
  }: GETSummaryStatsProps) => void;
  clearSummaryFilteringLoading: () => void;
}

export const useSummaryStats = create<IStore>()(
  persist(
    (set) => ({
      // State
      allTimeSummaryStats: [],
      yearlyLocationStats: [],
      monthlyLocationStats: [],
      weeklyLocationStats: [],
      lastTwelweMonthsLocationStats: [],
      allTimeSummaryStatsV2: [],
      yearlyLocationStatsV2: [],
      monthlyLocationStatsV2: [],
      weeklyLocationStatsV2: [],
      kpiStats: null,
      // Loaders
      isSummaryFilteringLoading: false,
      isKpiLoading: false,
      // Functions
      getSummaryStats: async () => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.get("summary/stats");
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on fetching Summary stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      recalculateAllTimeSummaryStats: async () => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.get("summary/stats/recalculate");
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on recalculating Summary stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      recalculateYearlySummaryStats: async () => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.post("summary/stats/recalculate/yearly");
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on recalculating Summary stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      recalculateMonthlySummaryStats: async (year: number, month: number) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.post(
            "summary/stats/recalculate/monthly",
            {
              year,
              month,
            }
          );
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on recalculating Summary stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      recalculateWeeklySummaryStats: async (year: number, month: number) => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.post(
            "summary/stats/recalculate/weekly",
            {
              year,
              month,
            }
          );
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on recalculating Summary stats", { error });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      recalculatelastTwelveMonthsSummaryStats: async () => {
        useAppLoading.getState().setIsLoading(true);
        try {
          const response = await axios.get(
            "summary/stats/recalculate/last-twelve-months"
          );
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            const lastTwelweMonths =
              response?.data?.filter(
                (summaryStat: ISummaryStats) =>
                  summaryStat?.filterType === "LAST_THIRTY_DAYS"
              ) ?? [];
            set({
              allTimeSummaryStats: allTime,
              yearlyLocationStats: yearly,
              monthlyLocationStats: monthly,
              weeklyLocationStats: weekly,
              lastTwelweMonthsLocationStats: lastTwelweMonths,
            });
          }
        } catch (error) {
          LOG.error("Error on recalculating Last Twelve Months Summary stats", {
            error,
          });
        } finally {
          useAppLoading.getState().setIsLoading(false);
        }
      },
      getSummaryStatsByFiltering: async ({
        period,
        year,
        fromYear,
        toYear,
        month,
        fromMonth,
        toMonth,
        fromWeek,
        toWeek,
        quarter,
        week,
        selectedFilters,
        selectedStores,
        storeType,
      }: GETSummaryStatsProps) => {
        set({ isSummaryFilteringLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (fromYear !== undefined) {
          params.append("fromYear", fromYear.toString());
        }
        if (toYear !== undefined) {
          params.append("toYear", toYear.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (fromMonth !== undefined) {
          params.append("fromMonth", fromMonth.toString());
        }
        if (toMonth !== undefined) {
          params.append("toMonth", toMonth.toString());
        }
        if (fromWeek !== undefined) {
          params.append("fromWeek", fromWeek.toString());
        }
        if (toWeek !== undefined) {
          params.append("toWeek", toWeek.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (storeType !== undefined) {
          params.append("storeType", storeType);
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/summary/filtering-stats?${params.toString()}`
          );
          if (response?.data) {
            const allTime =
              response?.data?.filter(
                (summaryStat: ISummaryStatsV2) =>
                  summaryStat?.filterType === "ALL"
              ) ?? [];
            const yearly =
              response?.data?.filter(
                (summaryStat: ISummaryStatsV2) =>
                  summaryStat?.filterType === "YEARLY"
              ) ?? [];
            const monthly =
              response?.data?.filter(
                (summaryStat: ISummaryStatsV2) =>
                  summaryStat?.filterType === "MONTHLY"
              ) ?? [];
            const weekly =
              response?.data?.filter(
                (summaryStat: ISummaryStatsV2) =>
                  summaryStat?.filterType === "WEEKLY"
              ) ?? [];
            set({
              allTimeSummaryStatsV2: allTime,
              yearlyLocationStatsV2: yearly,
              monthlyLocationStatsV2: monthly,
              weeklyLocationStatsV2: weekly,
            });
            set({ isSummaryFilteringLoading: false });
          }
        } catch (error) {
          set({ isSummaryFilteringLoading: false });
          LOG.error("Error on fetching active/inactive users", { error });
        }
      },
      getKPIStats: async ({
        period,
        year,
        fromYear,
        toYear,
        month,
        fromMonth,
        toMonth,
        fromWeek,
        toWeek,
        quarter,
        week,
        selectedFilters,
        selectedStores,
        storeType,
      }: GETSummaryStatsProps) => {
        set({ isKpiLoading: true });
        const currentLocation = useCurrentLocation.getState().currentLocation;

        // Build query parameters (same as getSummaryStatsByFiltering)
        const params = new URLSearchParams();
        params.append("period", period);
        if (year !== undefined) {
          params.append("year", year.toString());
        }
        if (fromYear !== undefined) {
          params.append("fromYear", fromYear.toString());
        }
        if (toYear !== undefined) {
          params.append("toYear", toYear.toString());
        }
        if (month !== undefined) {
          params.append("month", month.toString());
        }
        if (fromMonth !== undefined) {
          params.append("fromMonth", fromMonth.toString());
        }
        if (toMonth !== undefined) {
          params.append("toMonth", toMonth.toString());
        }
        if (fromWeek !== undefined) {
          params.append("fromWeek", fromWeek.toString());
        }
        if (toWeek !== undefined) {
          params.append("toWeek", toWeek.toString());
        }
        if (quarter !== undefined) {
          params.append("quarter", quarter);
        }
        if (week !== undefined) {
          params.append("week", week.toISOString());
        }
        if (storeType !== undefined) {
          params.append("storeType", storeType);
        }
        if (currentLocation) {
          params.append("location", currentLocation);
        }
        if (
          selectedFilters !== undefined &&
          !selectedFilters?.includes("all")
        ) {
          params.append("selectedStreams", selectedFilters.join(","));
        }
        if (
          selectedStores &&
          selectedStores.length > 0 &&
          !selectedStores.includes("all")
        ) {
          params.append("selectedStores", selectedStores.join(","));
        }

        try {
          const response = await axios.get(
            `/summary/filtering-stats/kpis?${params.toString()}`
          );
          if (response?.data) {
            set({ kpiStats: response.data as IKPIResponse, isKpiLoading: false });
          }
        } catch (error) {
          LOG.error("Error on fetching KPI stats", { error });
          set({ kpiStats: null, isKpiLoading: false });
        }
      },
      clearSummaryFilteringLoading: () => {
        set({ isSummaryFilteringLoading: false });
      },
    }),
    {
      name: "summary-stats-storage",
      storage: createJSONStorage(() => localStorage),
    }
  )
);
